import { getStrapiToken } from '@/services/auth/getStrapiToken';
// @/services/app/store/modules/ca-items-cache.js
/**
 * CAItemsCache - Lightweight in-memory cache for item+s (lead firm) data
 *
 * Purpose:
 * - Prefetch years list at login (when isLeadFirm detected)
 * - Cache year summaries to avoid re-fetching on navigation
 * - Cache tab data (filtered + unfiltered) with LRU eviction
 * - Session-scoped (clears on logout)
 *
 * NOT using CacheManager because:
 * - item+s data is specific to lead firms only
 * - We want simple in-memory cache, not sessionStorage
 * - Data invalidation is straightforward (manual trigger)
 */

/**
 * Generate a stable cache key from filter object.
 * Sorts keys alphabetically so {a:1,b:2} and {b:2,a:1} produce the same key.
 */
const stableFilterKey = (filters) => {
  if (!filters || typeof filters !== 'object') return '_unfiltered';
  const keys = Object.keys(filters).sort();
  if (keys.length === 0) return '_unfiltered';
  return keys
    .map((k) => {
      const v = filters[k];
      return `${k}=${Array.isArray(v) ? v.slice().sort().join('|') : v}`;
    })
    .join('&');
};

/**
 * LRU tab cache — stores API responses keyed by tab + year + filters.
 * Evicts oldest entries when maxEntries is exceeded per tab.
 */
class TabCache {
  constructor(maxEntriesPerTab = 20) {
    this.maxEntriesPerTab = maxEntriesPerTab;
    // Map<tabKey, Map<cacheKey, { data, timestamp }>>
    this.store = new Map();
  }

  _getTabStore(tab) {
    if (!this.store.has(tab)) {
      this.store.set(tab, new Map());
    }
    return this.store.get(tab);
  }

  get(tab, year, filters) {
    const tabStore = this._getTabStore(tab);
    const key = `${year}_${stableFilterKey(filters)}`;
    const entry = tabStore.get(key);
    if (!entry) {
      if (process.env.NODE_ENV === 'development') {
        console.log(`[CAItemsCache] MISS ${tab}_${key}`);
      }
      return null;
    }
    // Move to end (most recently used) by re-inserting
    tabStore.delete(key);
    tabStore.set(key, { ...entry, timestamp: Date.now() });
    if (process.env.NODE_ENV === 'development') {
      console.log(`[CAItemsCache] HIT  ${tab}_${key}`);
    }
    return entry.data;
  }

  set(tab, year, filters, data) {
    const tabStore = this._getTabStore(tab);
    const key = `${year}_${stableFilterKey(filters)}`;

    // Delete first so re-insert goes to end
    tabStore.delete(key);
    tabStore.set(key, { data, timestamp: Date.now() });

    // Evict oldest if over limit
    if (tabStore.size > this.maxEntriesPerTab) {
      const oldestKey = tabStore.keys().next().value;
      tabStore.delete(oldestKey);
      if (process.env.NODE_ENV === 'development') {
        console.log(`[CAItemsCache] EVICT ${tab}_${oldestKey} (${tabStore.size} entries)`);
      }
    }

    if (process.env.NODE_ENV === 'development') {
      console.log(`[CAItemsCache] SET  ${tab}_${key} (${tabStore.size} entries)`);
    }
  }

  clearTab(tab) {
    this.store.delete(tab);
  }

  clear() {
    this.store.clear();
  }
}

class CAItemsCache {
  constructor() {
    // In-memory cache
    this.years = null; // Array of years: [2025, 2024, ...]
    this.yearSummaries = {}; // { 2025: { totals: {}, byCategory: [] }, ... }

    // LRU tab cache (filtered + unfiltered data)
    this.tabCache = new TabCache(20);

    // Prefetch state
    this.prefetchPromise = null;
    this.lastFetch = null;
    this.CACHE_TTL = 5 * 60 * 1000; // 5 minutes

    // Subscribers for cache updates
    this.subscribers = new Set();

    // User preferences (persisted to localStorage)
    this.preferences = this._loadPreferences();
  }

  /**
   * Load preferences from localStorage
   * @private
   */
  _loadPreferences() {
    try {
      const stored = localStorage.getItem('ca-items-preferences');
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      // Ignore localStorage errors
    }
    // Defaults
    return {
      itemsPerPage: 12,
    };
  }

  /**
   * Save preferences to localStorage
   * @private
   */
  _savePreferences() {
    try {
      localStorage.setItem('ca-items-preferences', JSON.stringify(this.preferences));
    } catch (e) {
      // Ignore localStorage errors
    }
  }

  /**
   * Get items per page preference
   * @returns {number}
   */
  getItemsPerPage() {
    return this.preferences.itemsPerPage || 12;
  }

  /**
   * Set items per page preference
   * @param {number} value
   */
  setItemsPerPage(value) {
    this.preferences.itemsPerPage = value;
    this._savePreferences();
  }

  /**
   * Subscribe to cache updates
   * @param {Function} callback - Called when cache is updated
   * @returns {Function} Unsubscribe function
   */
  subscribe(callback) {
    this.subscribers.add(callback);
    return () => this.subscribers.delete(callback);
  }

  /**
   * Notify subscribers of cache update
   * @private
   */
  _notifySubscribers() {
    this.subscribers.forEach((cb) => {
      try {
        cb({ years: this.years, yearSummaries: this.yearSummaries });
      } catch (e) {
        // Don't let subscriber errors break the cache
      }
    });
  }

  // ──────────────────────────────────────────────────────
  // TAB DATA CACHE (LRU, supports filtered + unfiltered)
  // ──────────────────────────────────────────────────────

  /**
   * Get cached tab data for a tab + year + filter combination
   * @param {string} tab - Tab name (overview, categories, geography, organisation, suppliers)
   * @param {number|string} year
   * @param {Object} filters - Active filters (empty {} = unfiltered)
   * @returns {Object|null}
   */
  getTabData(tab, year, filters) {
    return this.tabCache.get(tab, year, filters);
  }

  /**
   * Store tab data in the LRU cache
   * @param {string} tab
   * @param {number|string} year
   * @param {Object} filters
   * @param {Object} data - API response to cache
   */
  setTabData(tab, year, filters, data) {
    this.tabCache.set(tab, year, filters, data);
  }

  /**
   * Check if cache is fresh
   * @returns {boolean}
   */
  isFresh() {
    return this.lastFetch && Date.now() - this.lastFetch < this.CACHE_TTL;
  }

  /**
   * Check if we have years cached (non-empty)
   * @returns {boolean}
   */
  hasYears() {
    return Array.isArray(this.years) && this.years.length > 0;
  }

  /**
   * Check if years have been fetched (could be empty array)
   * @returns {boolean}
   */
  hasFetched() {
    return Array.isArray(this.years);
  }

  /**
   * Get cached years (or null if not cached)
   * @returns {number[]|null}
   */
  getYears() {
    return this.years;
  }

  /**
   * Get cached summary for a year (or null if not cached)
   * @param {number} year
   * @returns {Object|null}
   */
  getYearSummary(year) {
    return this.yearSummaries[year] || null;
  }

  /**
   * Get all cached year data in the format the page expects
   * @returns {Array|null}
   */
  getYearsData() {
    if (!this.hasYears()) return null;

    return this.years
      .map((year) => {
        const summary = this.yearSummaries[year];
        if (!summary) return null;

        // Build categories array dynamically from all Scope 3 categories with data
        const categories = [];
        summary.byCategory?.forEach((cat) => {
          if (!cat.name) return;

          // Extract category number (e.g., "3.1" from "3.1 purchased goods & services")
          const match = cat.name.match(/^3\.(\d+)/);
          if (!match) return;

          const emissions = cat.emissionsSuppliers || 0;
          if (emissions > 0) {
            categories.push({
              key: `3.${match[1]}`,
              value: emissions,
              name: cat.name, // Full name for filter navigation
            });
          }
        });

        // Sort by category number (3.1, 3.2, 3.7, 3.10, etc.)
        categories.sort((a, b) => {
          const numA = parseFloat(a.key.replace('3.', ''));
          const numB = parseFloat(b.key.replace('3.', ''));
          return numA - numB;
        });

        return {
          year,
          totalEmissions: summary.totals?.emissionsSuppliers || 0,
          categories,
        };
      })
      .filter(Boolean)
      .sort((a, b) => b.year - a.year);
  }

  /**
   * Prefetch years and summaries for a project
   * Called at login when isLeadFirm() is detected
   *
   * @param {string|number} projectId
   * @returns {Promise<void>}
   */
  async prefetch(projectId) {
    // Return existing promise if prefetch already in progress
    if (this.prefetchPromise) {
      return this.prefetchPromise;
    }

    // Skip if cache is fresh
    if (this.isFresh() && this.hasYears()) {
      return;
    }

    if (!projectId) {
      if (process.env.NODE_ENV === 'development') {
        console.log('[CAItemsCache] No projectId, skipping prefetch');
      }
      return;
    }

    if (process.env.NODE_ENV === 'development') {
      console.log('[CAItemsCache] Starting prefetch for project:', projectId);
    }

    this.prefetchPromise = this._doPrefetch(projectId);

    try {
      await this.prefetchPromise;
    } finally {
      this.prefetchPromise = null;
    }
  }

  /**
   * Internal prefetch implementation
   * @private
   */
  async _doPrefetch(projectId) {
    try {
      // Fetch years first
      const yearsResponse = await fetch(
        `${process.env.GATSBY_STRAPI_URL}/api/ca-items-row/project/${projectId}/years`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getStrapiToken()}`,
          },
        }
      );

      if (!yearsResponse.ok) {
        throw new Error(`Failed to fetch years: ${yearsResponse.status}`);
      }

      const yearsData = await yearsResponse.json();
      this.years = yearsData.years || [];

      if (process.env.NODE_ENV === 'development') {
        console.log('[CAItemsCache] Years loaded:', this.years);
      }

      if (this.years.length === 0) {
        this.lastFetch = Date.now();
        this._notifySubscribers();
        return;
      }

      // Fetch summaries for each year in parallel
      const summaryPromises = this.years.map(async (year) => {
        try {
          const response = await fetch(
            `${process.env.GATSBY_STRAPI_URL}/api/ca-items-row/project/${projectId}/year/${year}/overview`,
            {
              method: 'GET',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${getStrapiToken()}`,
              },
            }
          );

          if (response.ok) {
            const summary = await response.json();
            this.yearSummaries[year] = summary;
          }
        } catch (e) {
          // Individual year summary failures don't break the whole prefetch
          if (process.env.NODE_ENV === 'development') {
            console.warn(`[CAItemsCache] Failed to fetch summary for ${year}:`, e);
          }
        }
      });

      await Promise.all(summaryPromises);

      this.lastFetch = Date.now();
      this._notifySubscribers();

      if (process.env.NODE_ENV === 'development') {
        console.log('[CAItemsCache] Prefetch complete:', {
          years: this.years,
          summariesLoaded: Object.keys(this.yearSummaries).length,
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.error('[CAItemsCache] Prefetch failed:', error);
      }
      // Don't throw - prefetch failures shouldn't break the app
    }
  }

  /**
   * Wait for prefetch to complete if in progress
   * @returns {Promise<void>}
   */
  async waitForPrefetch() {
    if (this.prefetchPromise) {
      await this.prefetchPromise;
    }
  }

  /**
   * Invalidate cache (call when data changes)
   */
  invalidate() {
    this.years = null;
    this.yearSummaries = {};
    this.tabCache.clear();
    this.lastFetch = null;
    this.prefetchPromise = null;

    if (process.env.NODE_ENV === 'development') {
      console.log('[CAItemsCache] Cache invalidated');
    }
  }

  /**
   * Clear cache (call on logout)
   */
  clear() {
    this.invalidate();
    this.subscribers.clear();

    // Also clear the filter options cache to ensure fresh data on next login
    try {
      const { clearFilterOptionsCache } = require('@/hooks/items/useFilterOptions');
      clearFilterOptionsCache();
    } catch (e) {
      // Hook may not be loaded yet, that's ok
    }
  }
}

// Export singleton
export const caItemsCache = new CAItemsCache();
